import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  PlusCircle,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  FileText,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import {
  API,
  ORIENTATION_LABELS,
  FOUNDER_LABELS,
  GOVERNANCE_LABELS,
  MONETIZATION_LABELS,
} from "../../lib/constants";

export const ContributeTab = ({ showNotification }) => {
  const [activeSection, setActiveSection] = useState("submit"); // submit | corrections | review
  const [correctionStats, setCorrectionStats] = useState(null);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, subsRes] = await Promise.all([
        axios.get(`${API}/corrections/stats`),
        axios.get(`${API}/submissions/recent`),
      ]);
      setCorrectionStats(statsRes.data);
      setRecentSubmissions(subsRes.data);
    } catch (e) {
      void 0; // error handled silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sections = [
    { id: "submit", label: "Submit App", icon: PlusCircle },
    { id: "corrections", label: "Corrections", icon: FileText },
    { id: "review", label: "Review Queue", icon: CheckCircle },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contribute-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6 text-primary" />
          Contribute to the Observatory
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Submit new AI apps, suggest corrections, and help improve data quality.
        </p>
      </div>

      {/* Stats cards */}
      {correctionStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatMini
            label="Total Corrections"
            value={correctionStats.total}
            icon={FileText}
            color="#3b82f6"
          />
          <StatMini
            label="Pending Review"
            value={correctionStats.pending}
            icon={Clock}
            color="#f59e0b"
          />
          <StatMini
            label="Approved"
            value={correctionStats.approved}
            icon={CheckCircle}
            color="#10b981"
          />
          <StatMini
            label="Rejected"
            value={correctionStats.rejected}
            icon={XCircle}
            color="#ef4444"
          />
        </div>
      )}

      {/* Section tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            data-testid={`contribute-tab-${s.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeSection === s.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <s.icon className="w-4 h-4" />
            {s.label}
          </button>
        ))}
      </div>

      {/* Submit App Section */}
      {activeSection === "submit" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-lg mb-4">Submit a New AI App</h3>
              <SubmitForm
                onSuccess={() => {
                  showNotification("App submitted successfully!", "success");
                  fetchData();
                }}
              />
            </div>
          </div>
          <div>
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-sm mb-3">Recent Submissions</h3>
              {recentSubmissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No submissions yet. Be the first!</p>
              ) : (
                <div className="space-y-3">
                  {recentSubmissions.map((app) => (
                    <div
                      key={app.id}
                      className="p-3 bg-muted/30 rounded-lg border border-border/50"
                    >
                      <p className="font-medium text-sm truncate">{app.product_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{app.tagline}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span className="px-1.5 py-0.5 rounded bg-muted/60">
                          {ORIENTATION_LABELS[app.value_orientation] || app.value_orientation}
                        </span>
                        {app.country && <span>{app.country}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Corrections Section */}
      {activeSection === "corrections" && (
        <CorrectionsList
          corrections={correctionStats?.recent || []}
          onRefresh={fetchData}
          showNotification={showNotification}
          readOnly
        />
      )}

      {/* Review Queue Section */}
      {activeSection === "review" && (
        <CorrectionsList
          corrections={(correctionStats?.recent || []).filter((c) => c.status === "pending")}
          onRefresh={fetchData}
          showNotification={showNotification}
          readOnly={false}
        />
      )}
    </div>
  );
};

/* ── Submit Form ── */
const SubmitForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    product_name: "",
    website_url: "",
    product_hunt_url: "",
    tagline: "",
    description: "",
    launch_date: "",
    value_orientation: "unknown",
    governance_framing: "unknown",
    monetization_model: "unknown",
    founder_composition: "unknown",
    founder_names: "",
    country: "",
    submitted_by: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!formData.product_name.trim()) e.product_name = "Required";
    if (!formData.tagline.trim()) e.tagline = "Required";
    if (!formData.launch_date) e.launch_date = "Required";
    if (formData.website_url && !formData.website_url.startsWith("http"))
      e.website_url = "Must start with http";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/apps`, formData);
      onSuccess();
      setFormData({
        product_name: "",
        website_url: "",
        product_hunt_url: "",
        tagline: "",
        description: "",
        launch_date: "",
        value_orientation: "unknown",
        governance_framing: "unknown",
        monetization_model: "unknown",
        founder_composition: "unknown",
        founder_names: "",
        country: "",
        submitted_by: "",
      });
      setErrors({});
    } catch (error) {
      void 0; // error handled silently
    } finally {
      setSubmitting(false);
    }
  };

  const inp =
    "w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors";
  const lbl = "block text-sm font-medium mb-1.5";

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contribute-submit-form">
      {/* Row 1: Name + Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>
            Product Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.product_name}
            onChange={(e) => setFormData((f) => ({ ...f, product_name: e.target.value }))}
            className={`${inp} ${errors.product_name ? "border-red-500" : ""}`}
            placeholder="e.g., MindfulAI"
            data-testid="contribute-input-name"
          />
          {errors.product_name && (
            <p className="text-xs text-red-400 mt-1">{errors.product_name}</p>
          )}
        </div>
        <div>
          <label className={lbl}>
            Launch Date <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            value={formData.launch_date}
            onChange={(e) => setFormData((f) => ({ ...f, launch_date: e.target.value }))}
            className={`${inp} ${errors.launch_date ? "border-red-500" : ""}`}
            data-testid="contribute-input-date"
          />
          {errors.launch_date && (
            <p className="text-xs text-red-400 mt-1">{errors.launch_date}</p>
          )}
        </div>
      </div>

      {/* Row 2: Tagline */}
      <div>
        <label className={lbl}>
          Tagline <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={formData.tagline}
          onChange={(e) => setFormData((f) => ({ ...f, tagline: e.target.value }))}
          className={`${inp} ${errors.tagline ? "border-red-500" : ""}`}
          placeholder="Brief description of the product"
          data-testid="contribute-input-tagline"
        />
        {errors.tagline && <p className="text-xs text-red-400 mt-1">{errors.tagline}</p>}
      </div>

      {/* Row 3: Description */}
      <div>
        <label className={lbl}>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
          rows={3}
          placeholder="What does this app do?"
          className={inp}
          data-testid="contribute-input-desc"
        />
      </div>

      {/* Row 4: URLs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Website URL</label>
          <input
            type="url"
            value={formData.website_url}
            onChange={(e) => setFormData((f) => ({ ...f, website_url: e.target.value }))}
            className={`${inp} ${errors.website_url ? "border-red-500" : ""}`}
            placeholder="https://..."
            data-testid="contribute-input-url"
          />
          {errors.website_url && (
            <p className="text-xs text-red-400 mt-1">{errors.website_url}</p>
          )}
        </div>
        <div>
          <label className={lbl}>Product Hunt URL</label>
          <input
            type="url"
            value={formData.product_hunt_url}
            onChange={(e) => setFormData((f) => ({ ...f, product_hunt_url: e.target.value }))}
            className={inp}
            placeholder="https://producthunt.com/..."
            data-testid="contribute-input-ph"
          />
        </div>
      </div>

      {/* Row 5: Classification */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SelectField
          label="Value Orientation"
          value={formData.value_orientation}
          onChange={(v) => setFormData((f) => ({ ...f, value_orientation: v }))}
          options={ORIENTATION_LABELS}
          testId="contribute-select-orientation"
        />
        <SelectField
          label="Governance Framing"
          value={formData.governance_framing}
          onChange={(v) => setFormData((f) => ({ ...f, governance_framing: v }))}
          options={GOVERNANCE_LABELS}
          testId="contribute-select-governance"
        />
        <SelectField
          label="Monetization"
          value={formData.monetization_model}
          onChange={(v) => setFormData((f) => ({ ...f, monetization_model: v }))}
          options={MONETIZATION_LABELS}
          testId="contribute-select-monetization"
        />
      </div>

      {/* Row 6: Founder + Country */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SelectField
          label="Founder Composition"
          value={formData.founder_composition}
          onChange={(v) => setFormData((f) => ({ ...f, founder_composition: v }))}
          options={FOUNDER_LABELS}
          testId="contribute-select-founder"
        />
        <div>
          <label className={lbl}>Country</label>
          <input
            type="text"
            value={formData.country}
            onChange={(e) => setFormData((f) => ({ ...f, country: e.target.value }))}
            className={inp}
            placeholder="e.g., USA, India"
            data-testid="contribute-input-country"
          />
        </div>
        <div>
          <label className={lbl}>Founder Names</label>
          <input
            type="text"
            value={formData.founder_names}
            onChange={(e) => setFormData((f) => ({ ...f, founder_names: e.target.value }))}
            className={inp}
            placeholder="Comma-separated"
            data-testid="contribute-input-founders"
          />
        </div>
      </div>

      {/* Row 7: Submitted by */}
      <div>
        <label className={lbl}>Your Name (optional)</label>
        <input
          type="text"
          value={formData.submitted_by}
          onChange={(e) => setFormData((f) => ({ ...f, submitted_by: e.target.value }))}
          className={inp}
          placeholder="For attribution"
          data-testid="contribute-input-submitter"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="contribute-submit-btn"
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 font-medium transition-colors"
      >
        {submitting ? <div className="spinner" /> : <Send className="w-4 h-4" />}
        {submitting ? "Submitting..." : "Submit App"}
      </button>
    </form>
  );
};

/* ── Corrections List ── */
const CorrectionsList = ({ corrections, onRefresh, showNotification, readOnly }) => {
  const [processing, setProcessing] = useState(null);

  const handleAction = async (id, action) => {
    setProcessing(id);
    try {
      await axios.post(`${API}/corrections/${id}/${action}`);
      showNotification(`Correction ${action}d`, "success");
      onRefresh();
    } catch (e) {
      showNotification(`Error: ${e.response?.data?.detail || e.message}`, "error");
    } finally {
      setProcessing(null);
    }
  };

  if (corrections.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
        <p className="text-muted-foreground">
          {readOnly ? "No corrections submitted yet." : "No corrections pending review."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="corrections-list">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="text-left p-3 font-medium">App</th>
            <th className="text-left p-3 font-medium">Field</th>
            <th className="text-left p-3 font-medium">Current</th>
            <th className="text-left p-3 font-medium">Suggested</th>
            <th className="text-left p-3 font-medium">Status</th>
            {!readOnly && <th className="text-left p-3 font-medium">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {corrections.map((c, idx) => (
            <tr
              key={c.id}
              className={`border-b border-border/50 ${idx % 2 ? "bg-muted/10" : ""}`}
            >
              <td className="p-3">
                <p className="font-medium truncate max-w-[180px]">{c.app_name || c.app_id}</p>
                {c.reason && (
                  <p className="text-xs text-muted-foreground truncate max-w-[180px]">{c.reason}</p>
                )}
              </td>
              <td className="p-3 text-muted-foreground">{c.field_name}</td>
              <td className="p-3">
                <span className="px-2 py-0.5 rounded bg-muted/60 text-xs">{c.current_value || "—"}</span>
              </td>
              <td className="p-3">
                <span className="px-2 py-0.5 rounded bg-primary/15 text-primary text-xs font-medium">
                  {c.suggested_value}
                </span>
              </td>
              <td className="p-3">
                <StatusBadge status={c.status} />
              </td>
              {!readOnly && c.status === "pending" && (
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleAction(c.id, "approve")}
                      disabled={processing === c.id}
                      data-testid={`approve-${c.id}`}
                      className="p-1.5 hover:bg-green-500/10 rounded text-green-400 transition-colors"
                      title="Approve"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleAction(c.id, "reject")}
                      disabled={processing === c.id}
                      data-testid={`reject-${c.id}`}
                      className="p-1.5 hover:bg-red-500/10 rounded text-red-400 transition-colors"
                      title="Reject"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              )}
              {!readOnly && c.status !== "pending" && <td className="p-3">—</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── Helpers ── */
const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
      data-testid={testId}
    >
      {Object.entries(options).map(([k, v]) => (
        <option key={k} value={k}>
          {v}
        </option>
      ))}
    </select>
  </div>
);

const StatMini = ({ label, value, icon: Icon, color }) => (
  <div className="bg-card border border-border rounded-xl p-4" data-testid={`contribute-stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-bold mt-0.5" style={{ color }}>
          {value}
        </p>
      </div>
      <div className="p-2 rounded-lg" style={{ backgroundColor: color + "15" }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    approved: "bg-green-500/10 text-green-400 border-green-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}
    >
      {status === "pending" && <Clock className="w-3 h-3" />}
      {status === "approved" && <CheckCircle className="w-3 h-3" />}
      {status === "rejected" && <XCircle className="w-3 h-3" />}
      {status}
    </span>
  );
};
